import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Form, Spinner, Alert } from 'react-bootstrap';
import { PlusLg, PencilSquare, Trash } from 'react-bootstrap-icons';
import Swal from 'sweetalert2';
import DashboardLayout from '../components/DashboardLayout';
import { apiRequest } from '../utils/api';
import { getActiveCompanyId } from '../utils/companyContext';
import { useNavigate } from 'react-router-dom';
import { getModulePermissions } from '../utils/permissions';
import '../assets/styles/files.css';

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });

const defaultFormState = {
  fileName: '',
  description: '',
  status: 'Active',
};

const FileNameManager = () => {
  const navigate = useNavigate();
  const [fileNames, setFileNames] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [formData, setFormData] = useState(defaultFormState);
  const [isSaving, setIsSaving] = useState(false);
  const [activeCompanyId, setActiveCompanyId] = useState(() => getActiveCompanyId());
  const permissions = useMemo(() => getModulePermissions('employee.files'), []);
  const canCreate = permissions.create;
  const canEdit = permissions.edit;
  const canDelete = permissions.delete;

  const actionButtonGroupClass = useMemo(
    () =>
      [
        'd-flex',
        'flex-column',
        'flex-md-row',
        'justify-content-md-end',
        'align-items-stretch',
        'align-items-md-center',
        'gap-3',
        'mb-4',
      ].join(' '),
    [],
  );

  const isSaveDisabled = useMemo(
    () => formData.fileName.trim().length === 0 || isSaving,
    [formData.fileName, isSaving],
  );

  const loadFileNames = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');

      if (!activeCompanyId) {
        setFileNames([]);
        setError('Select a company to manage file names.');
        return;
      }

      const params = new URLSearchParams();
      params.set('companyId', activeCompanyId);

      const { data } = await apiRequest(`/api/admin/files/names?${params.toString()}`);
      setFileNames(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load file names.');
      setFileNames([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeCompanyId]);

  useEffect(() => {
    loadFileNames();
  }, [loadFileNames]);

  useEffect(() => {
    const handleActiveCompanyChanged = (event) => {
      const nextCompanyId = event?.detail?.id ?? getActiveCompanyId();
      setActiveCompanyId(nextCompanyId);
    };

    window.addEventListener('activeCompanyChanged', handleActiveCompanyChanged);

    return () => {
      window.removeEventListener('activeCompanyChanged', handleActiveCompanyChanged);
    };
  }, []);

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingFile(null);
    setFormData(defaultFormState);
    setIsSaving(false);
  };

  const handleCreateClick = () => {
    setEditingFile(null);
    setFormData(defaultFormState);
    setShowModal(true);
  };

  const handleEditClick = (file) => {
    setEditingFile(file);
    setFormData({
      fileName: file.fileName,
      description: file.description ?? '',
      status: file.status ?? 'Active',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!canDelete) {
      window.alert('You do not have permission to delete file names.');
      return;
    }

    const result = await Swal.fire({
      title: 'Delete file name?',
      text: 'Are you sure you want to delete this file name? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete',
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await apiRequest(`/api/admin/files/names/${id}`, { method: 'DELETE' });
      setFileNames((prev) => prev.filter((file) => file.id !== id));
    } catch (deleteError) {
      window.alert(deleteError.message || 'Failed to delete the file name.');
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedFileName = formData.fileName.trim();

    if (!trimmedFileName) {
      return;
    }

    try {
      setIsSaving(true);

      const payload = {
        fileName: trimmedFileName,
        description: formData.description.trim(),
        status: formData.status,
      };

      if (editingFile) {
        const { data } = await apiRequest(`/api/admin/files/names/${editingFile.id}`, {
          method: 'PUT',
          body: payload,
        });

        setFileNames((prev) =>
          prev
            .map((file) => (file.id === data.id ? data : file))
            .sort((a, b) => a.fileName.localeCompare(b.fileName)),
        );
      } else {
        const { data } = await apiRequest('/api/admin/files/names', {
          method: 'POST',
          body: payload,
        });
        setFileNames((prev) =>
          [...prev, data].sort((a, b) => a.fileName.localeCompare(b.fileName)),
        );
      }

      handleCloseModal();
    } catch (saveError) {
      window.alert(saveError.message || 'Failed to save the file name.');
      setIsSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="files-page container-fluid py-4">
        <div className={actionButtonGroupClass}>
          <button
            className="files-pill-button d-flex align-items-center justify-content-center"
            onClick={() => navigate('/files')}
            type="button"
          >
            Files
          </button>
          {canCreate && (
            <button
              className="files-pill-button d-flex align-items-center justify-content-center"
              onClick={handleCreateClick}
              type="button"
            >
              <PlusLg className="me-2" />
              Create File Name
            </button>
          )}
          <button
            className="files-pill-button d-flex align-items-center justify-content-center"
            onClick={() => navigate('/resume')}
            type="button"
          >
            Resumes
          </button>
        </div>

        <div className="card files-table-card shadow-sm">
          <div className="card-body p-0">
            {isLoading ? (
              <div className="py-5 text-center">
                <Spinner animation="border" role="status" />
              </div>
            ) : error ? (
              <div className="py-5 px-3">
                <Alert variant="danger" className="mb-0">
                  {error}
                </Alert>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0 files-table align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: '5%' }}>#</th>
                      <th>File Name</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th>Created At</th>
                      <th>Updated At</th>
                      <th className="text-center" style={{ width: '12%' }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {fileNames.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-4">
                          No file names found. Click "Create File Name" to add one.
                        </td>
                      </tr>
                    ) : (
                      fileNames.map((file, index) => (
                        <tr key={file.id}>
                          <td>{index + 1}</td>
                          <td className="fw-semibold">{file.fileName}</td>
                          <td>{file.description || '-'}</td>
                          <td>
                            <span
                              className={`badge rounded-pill ${
                                file.status === 'Active'
                                  ? 'bg-success-subtle text-success'
                                  : 'bg-secondary-subtle text-secondary'
                              }`}
                            >
                              {file.status}
                            </span>
                          </td>
                          <td>{formatDate(file.createdAt)}</td>
                          <td>{formatDate(file.updatedAt)}</td>
                          <td>
                          <div className="files-action-buttons d-flex justify-content-center gap-2">
                            {canEdit && (
                              <button
                                type="button"
                                className="btn btn-icon"
                                onClick={() => handleEditClick(file)}
                                aria-label={`Edit ${file.fileName}`}
                              >
                                <PencilSquare />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                type="button"
                                className="btn btn-icon"
                                onClick={() => handleDelete(file.id)}
                                aria-label={`Delete ${file.fileName}`}
                              >
                                <Trash />
                              </button>
                            )}
                          </div>
                        </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 d-flex justify-content-center">
          <button
            type="button"
            className="files-pill-button d-flex align-items-center justify-content-center"
            onClick={() => navigate('/files')}
          >
            Back to Files
          </button>
        </div>

        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Form onSubmit={handleSubmit}>
            <Modal.Header closeButton>
              <Modal.Title>
                {editingFile ? 'Edit File Name' : 'Create File Name'}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group className="mb-3" controlId="fileName">
                <Form.Label>
                  File Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="fileName"
                  value={formData.fileName}
                  onChange={handleChange}
                  placeholder="Enter file name"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="description">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Add a brief description"
                />
              </Form.Group>
              <Form.Group className="mb-0" controlId="status">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </Form.Select>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-danger" disabled={isSaveDisabled}>
                {isSaving ? 'Saving…' : editingFile ? 'Update' : 'Create'}
              </button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    </DashboardLayout>
  );
};

export default FileNameManager;
